import { clsx } from 'clsx';
import { FC, forwardRef, ReactNode } from 'react';
import DotFill from '@o2/icons/DotFill';
import Stack, { StackProps } from '@o2/ui/Stack';
import colors from '@o2/ui/theme/colors';
import palette from '@o2/ui/theme/palette';
import Typography from '@o2/ui/Typography';

import { useInlineStateUtilityClasses } from './inlineStateClasses';

export interface InlineStateProps extends StackProps {
  color?: 'default' | 'neutral' | 'warning' | 'success' | 'error';
  label: ReactNode;
}

const getIconColor = (color: InlineStateProps['color']) => {
  if (!color || color === 'default') {
    return colors.transparentGray['400'];
  }

  return palette[color].main ?? palette.text.secondary;
};

const getTextColor = (color: InlineStateProps['color']) => {
  switch (color) {
    case 'default':
      return palette['neutral'].dark;
    default:
      return color ? palette[color].dark : palette['neutral'].dark;
  }
};

const InlineState: FC<InlineStateProps> = forwardRef(({ classes, color = 'default', label, ...props }, ref) => {
  const utilityClasses = useInlineStateUtilityClasses(classes);

  return (
    <Stack className={clsx(utilityClasses.root)} direction="row" alignItems="baseline" spacing={1} {...props} ref={ref}>
      <DotFill className={clsx(utilityClasses.icon)} sx={{ fontSize: '10px', color: getIconColor(color) }} />
      <Typography className={clsx(utilityClasses.label)} color={getTextColor(color)} component="span">
        {label}
      </Typography>
    </Stack>
  );
});

InlineState.displayName = 'InlineState';

export default InlineState;
